import React, { useState, useEffect } from 'react';
import {
  IndianRupee,
  Clock,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Calculator,
  Loader2,
  Eye
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { incentivesAPI, dealsAPI, demosAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { usePrivacy } from '../../contexts/PrivacyContext';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    // dateString is YYYY-MM
    const [year, month] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const getStatusColor = (status) => {
  const colors = {
    PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
    APPROVED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
    PAYABLE: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400',
    PAID: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
    EXPECTED: 'bg-slate-100 text-slate-700 dark:bg-slate-900/50 dark:text-slate-400',
  };
  return colors[status] || 'bg-slate-100 text-slate-700';
};

const UpcomingPayouts = () => {
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const [loading, setLoading] = useState(true);
  const [upcomingIncentives, setUpcomingIncentives] = useState([]);
  
  // Detail modal state
  const [selectedIncentive, setSelectedIncentive] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [dealDetails, setDealDetails] = useState(null);
  const [demoDetails, setDemoDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    const fetchUpcoming = async () => {
      try {
        setLoading(true);
        const response = await incentivesAPI.listRecords({ limit: 500 });
        const records = Array.isArray(response.data) ? response.data : (response.data?.records || []);

        // Filter for future dates (Asia/Kolkata calendar month)
        const now = new Date();
        const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        const currentMonthStr = `${istNow.getFullYear()}-${String(istNow.getMonth() + 1).padStart(2, '0')}`;

        const future = records.filter(rec => (rec.period_month || '') > currentMonthStr);
        future.sort((a, b) => a.period_month.localeCompare(b.period_month));

        setUpcomingIncentives(future);
      } catch (error) {
        console.error('Failed to fetch upcoming incentives:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUpcoming();
  }, []);

  // View incentive details with calculation
  const handleViewDetails = async (incentive) => {
    setSelectedIncentive(incentive);
    setDetailModalOpen(true);
    setLoadingDetails(true);
    setDealDetails(null);
    setDemoDetails(null);
    
    try {
      // Fetch deal or demo details
      if (incentive.deal_id) {
        const dealRes = await dealsAPI.get(incentive.deal_id);
        setDealDetails(dealRes.data);
      }
      if (incentive.demo_id) {
        const demoRes = await demosAPI.get(incentive.demo_id);
        setDemoDetails(demoRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Build calculation breakdown for the modal
  const getCalculationBreakdown = () => {
    if (!selectedIncentive) return null;
    
    if (selectedIncentive.metric_type === 'SQD') {
      return {
        type: 'SQD Incentive',
        formula: 'SQD Count × Rate per SQD',
        components: [
          { label: 'SQD Count', value: '1' },
          { label: 'Rate per SQD', value: formatCurrency(selectedIncentive.amount_earned) },
        ],
        total: selectedIncentive.amount_earned
      };
    }
    
    if (selectedIncentive.metric_type === 'DEAL_CLOSED' && dealDetails) {
      let planAmount = 0;
      let setupFee = 0;
      
      if (dealDetails.line_items) {
        for (const item of dealDetails.line_items) {
          const desc = (item.description || '').toLowerCase();
          if (desc.includes('plan') || desc.includes('subscription')) {
            planAmount = item.total_price || item.unit_price || 0;
          } else if (desc.includes('setup') || desc.includes('one-time') || desc.includes('onetime')) {
            setupFee = item.total_price || item.unit_price || 0;
          }
        }
      }
      
      if (planAmount === 0) {
        planAmount = dealDetails.mrr || 0;
      }
      
      return {
        type: 'Deal Conversion Incentive',
        formula: '(Plan × 1st Month %) + (Setup Fee × Setup %)',
        components: [
          { label: 'Plan Subscription Amount', value: formatCurrency(planAmount), bold: true },
          { label: 'One-time Setup Fee', value: formatCurrency(setupFee), bold: true },
        ],
        total: selectedIncentive.amount_earned
      };
    }
    
    if (selectedIncentive.metric_type === 'RENEWAL' && dealDetails) {
      const planAmount = dealDetails.mrr || 0;
      
      return {
        type: 'Renewal Incentive',
        formula: 'Monthly Plan Amount × Renewal %',
        components: [
          { label: 'Monthly Plan Amount', value: formatCurrency(planAmount), bold: true },
          { label: 'Renewal Period', value: selectedIncentive.period_month },
        ],
        total: selectedIncentive.amount_earned
      };
    }
    
    return {
      type: selectedIncentive.metric_type,
      formula: 'Custom calculation',
      components: [
        { label: 'Amount Earned', value: formatCurrency(selectedIncentive.amount_earned) }
      ],
      total: selectedIncentive.amount_earned
    };
  };

  if (loading) {
    return <div className="p-4 text-center text-slate-500">Loading upcoming payouts...</div>;
  }

  return (
    <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                    <p className="text-sm font-medium text-blue-600">Total Upcoming</p>
                    <p className="text-2xl font-bold text-blue-700">
                        {formatCurrency(upcomingIncentives.reduce((sum, i) => sum + i.amount_earned, 0))}
                    </p>
                </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-6">
                    <p className="text-sm font-medium text-purple-600">Future Count</p>
                    <p className="text-2xl font-bold text-purple-700">{upcomingIncentives.length}</p>
                </CardContent>
            </Card>
        </div>

        <Card className="bg-white border-slate-200">
            <CardContent className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Due Month</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Projected Amount</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-[80px]">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {upcomingIncentives.length > 0 ? (
                            upcomingIncentives.map(inc => (
                                <TableRow 
                                    key={inc.id}
                                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900"
                                    onClick={() => handleViewDetails(inc)}
                                >
                                    <TableCell className="font-medium">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-slate-400" />
                                            {formatDate(inc.period_month)}
                                        </div>
                                    </TableCell>
                                    <TableCell>{inc.company_name}</TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className={cn(
                                            inc.metric_type === 'SQD' && 'bg-blue-50 text-blue-700',
                                            inc.metric_type === 'RENEWAL' && 'bg-emerald-50 text-emerald-700',
                                            inc.metric_type === 'DEAL_CLOSED' && 'bg-purple-50 text-purple-700'
                                        )}>
                                            {inc.metric_type}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="font-bold text-slate-700">
                                        {formatCurrency(inc.amount_earned)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={getStatusColor(inc.status)}>
                                            <Clock className="w-3 h-3 mr-1" /> {inc.status || 'Scheduled'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => handleViewDetails(inc)} 
                                            title="View Details"
                                        >
                                            <Eye className="w-4 h-4 text-slate-500" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                                    No upcoming payouts scheduled
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>

        {/* Detail Modal with Calculation */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-purple-500" />
                        Incentive Calculation Details
                    </DialogTitle>
                    <DialogDescription>
                        Detailed breakdown of this upcoming incentive
                    </DialogDescription>
                </DialogHeader>
                
                {loadingDetails ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                ) : selectedIncentive ? (
                    <div className="space-y-4">
                        {/* Summary */}
                        <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs text-slate-500">Company</p>
                                    <p className="font-medium">{selectedIncentive.company_name}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Employee</p>
                                    <p className="font-medium">{selectedIncentive.user_name || 'You'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Type</p>
                                    <Badge variant="outline">{selectedIncentive.metric_type}</Badge>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Due Period</p>
                                    <p className="font-medium">{formatDate(selectedIncentive.period_month)}</p>
                                </div>
                            </div>
                        </div>

                        {/* Calculation Breakdown */}
                        {(() => {
                            const breakdown = getCalculationBreakdown();
                            if (!breakdown) return null;
                            
                            return (
                                <div className="space-y-3">
                                    <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                                        <p className="text-sm font-medium text-purple-700 dark:text-purple-300">{breakdown.type}</p>
                                        <p className="text-xs text-purple-600 dark:text-purple-400">Formula: {breakdown.formula}</p>
                                    </div>
                                    
                                    <div className="border rounded-lg divide-y dark:border-slate-700 dark:divide-slate-700">
                                        {breakdown.components.map((comp, idx) => (
                                            <div 
                                                key={idx} 
                                                className={cn(
                                                    "flex justify-between items-center p-3",
                                                    comp.bold && "font-medium"
                                                )}
                                            >
                                                <span className="text-sm text-slate-600 dark:text-slate-400">
                                                    {comp.label}
                                                </span>
                                                <span className="font-medium text-slate-800 dark:text-slate-200">
                                                    {comp.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <div className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg text-white">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium">Projected Amount</span>
                                            <span className="text-2xl font-bold">{formatCurrency(breakdown.total)}</span>
                                        </div>
                                        <p className="text-xs mt-1 text-blue-100">
                                            This is a projected amount. Final amount may vary based on plan changes.
                                        </p>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Deal Details */}
                        {dealDetails && (
                            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                                <p className="text-sm font-medium mb-2">Deal Details</p>
                                <div className="text-xs space-y-1">
                                    <p><span className="text-slate-500">Company:</span> {dealDetails.company_name}</p>
                                    <p><span className="text-slate-500">Plan:</span> {dealDetails.plan_name || 'Custom'}</p>
                                    <p><span className="text-slate-500">MRR:</span> {formatCurrency(dealDetails.mrr)}</p>
                                    {dealDetails.line_items?.map((item, i) => (
                                        <p key={i}><span className="text-slate-500">{item.description}:</span> {formatCurrency(item.total_price)}</p>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Status */}
                        <div className="flex justify-between items-center pt-3 border-t dark:border-slate-700">
                            <span className="text-sm text-slate-500">Current Status</span>
                            <Badge className={getStatusColor(selectedIncentive.status)}>
                                {selectedIncentive.status || 'EXPECTED'}
                            </Badge>
                        </div>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    </div>
  );
};

export default UpcomingPayouts;
